/**
 * Claims (Klärungen & Schäden) API hooks — mirrors services/case.ts.
 *
 * Uses the generated OpenAPI client ($api). The /claims/* paths and the Claim*
 * schemas only exist in src/generated/v1 AFTER the backend is restarted with
 * df_claim + df_clientportal_api's new api/claim routes and `npm run codegen`
 * has been re-run. Until then tsc reports the /claims/* paths as unknown — this
 * is the expected codegen gate (see project rule: never commit FE hooks on
 * generated types without running codegen).
 */
import { components, paths } from 'src/generated/v1';
import { useQueryClient } from '@tanstack/react-query';
import { $api } from './api';

export type Claim = components['schemas']['Claim'];
export type ClaimCreate = components['schemas']['ClaimCreate'];
export type ClaimUpdate = components['schemas']['ClaimUpdate'];
export type ClaimDocumentIn = components['schemas']['ClaimDocumentIn'];
export type EligibleOrder = components['schemas']['EligibleOrder'];
export type ClaimType = ClaimCreate['claim_type'];

/**
 * i18n key for a claim's status badge. A clarification (Paketklärung) in the
 * `submitted` state is shown as "Klärungsanfrage" (it is pushed to weship-v2 as
 * an investigation carrying the `clarification` status); every other case uses
 * the plain status label.
 */
export function claimStatusLabelKey(claimType?: string | null, status?: string | null): string {
  if (claimType === 'clarification' && status === 'submitted') {
    return 'claims.status_clarification_request';
  }
  return `claims.status_${status}`;
}

/**
 * Consolidated status groups. Flow's status enum is finer-grained than what the
 * customer needs to see, so the list + tabs collapse the real statuses into four
 * buckets; the detail overlay still shows the actual status. The grouping mirrors
 * weship-v2's mapping (df_claim_external_sync.V2_STATUS_MAP): the carrier
 * sub-states (approved/carrier_inquiry/carrier_response/objection) already arrive
 * as `at_carrier`, so they need no extra handling here.
 */
export type ClaimStatusGroup = 'submitted' | 'documents_missing' | 'at_carrier' | 'closed';

export const CLAIM_STATUS_GROUPS: { key: ClaimStatusGroup; statuses: string[] }[] = [
  { key: 'submitted', statuses: ['submitted', 'in_progress'] },
  { key: 'documents_missing', statuses: ['documents_missing'] },
  { key: 'at_carrier', statuses: ['at_carrier'] },
  { key: 'closed', statuses: ['credit_issued', 'resolved', 'rejected', 'closed'] },
];

const STATUS_TO_GROUP: Record<string, ClaimStatusGroup> = CLAIM_STATUS_GROUPS.reduce(
  (acc, group) => {
    group.statuses.forEach(status => {
      acc[status] = group.key;
    });
    return acc;
  },
  {} as Record<string, ClaimStatusGroup>,
);

export function claimStatusGroup(status?: string | null): ClaimStatusGroup {
  return (status && STATUS_TO_GROUP[status]) || 'submitted';
}

export function claimStatusGroupLabelKey(status?: string | null): string {
  return `claims.group_${claimStatusGroup(status)}`;
}

export function isClaimClosed(status?: string | null): boolean {
  return claimStatusGroup(status) === 'closed';
}

/**
 * Outcome of a closed claim — accepted vs. rejected — for the detail overlay.
 * Returns null while the claim is still open.
 */
export function claimOutcome(status?: string | null, resolutionType?: string | null): 'accepted' | 'rejected' | null {
  if (!isClaimClosed(status)) return null;
  if (status === 'rejected' || resolutionType === 'rejected') return 'rejected';
  return 'accepted';
}

export const useClaimsQuery = (props: paths['/claims/claim']['get']['parameters']['query']) => {
  const { tabIndex, ...filteredProps } = (props as any) ?? {};
  return $api.useQuery('get', '/claims/claim', {
    params: {
      query: filteredProps,
    },
  });
};

export const useClaimQuery = (id: number, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    `/claims/claim/{claim_id}`,
    {
      params: {
        path: {
          claim_id: id,
        },
      },
    },
    {
      ...options,
    },
  );
};

export const useClaimTypesQuery = () => {
  return $api.useQuery('get', '/claims/types');
};

export const useEligibleOrdersQuery = (search: string) => {
  return $api.useQuery('get', '/claims/eligible_orders', {
    params: {
      query: { search, limit: 20 },
    },
  });
};

export function invalidateClaimQueries(queryClient: ReturnType<typeof useQueryClient>) {
  const keys = [
    ['get', '/claims/claim'],
    ['get', `/claims/claim/{claim_id}`],
  ];
  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: key });
  });
}

export const useClaimMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = $api.useMutation('post', '/claims/claim', {
    onSuccess() {
      invalidateClaimQueries(queryClient);
    },
  });

  const updateMutation = $api.useMutation('put', '/claims/claim/{claim_id}', {
    onSuccess() {
      invalidateClaimQueries(queryClient);
    },
  });

  const deleteMutation = $api.useMutation('delete', '/claims/claim/{claim_id}', {
    onSuccess() {
      invalidateClaimQueries(queryClient);
    },
  });

  const messageMutation = $api.useMutation('post', '/claims/claim/{claim_id}/message', {
    onSuccess() {
      invalidateClaimQueries(queryClient);
    },
  });

  const documentMutation = $api.useMutation('post', '/claims/claim/{claim_id}/document', {
    onSuccess() {
      invalidateClaimQueries(queryClient);
    },
  });

  return { createMutation, updateMutation, deleteMutation, messageMutation, documentMutation };
};
